/* eslint-disable complexity */
import React, { useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { createStructuredSelector } from 'reselect';
import { PaxSelectorContentRow } from './ContentRow';
import { ABUtils, TrackingUtils, withPageContext, isAgeSelectorEnabled } from '../../utils';
import {
    TrainMaxPaxProps,
    TrainMaxPaxPropsDefaults,
    TrainPaxProps,
    TrainPaxAgesProps,
    TrainMaxPaxAgesPropsDefaults,
    TrainMaxPaxAgesProps,
    TrainMinPaxAgesPropsDefaults,
    TrainMinPaxAgesProps,
    TrainPaxErrorsProps,
    TrainPaxErrorsPropsDefaults,
} from '../../models';
import { setTrainPax, setTrainPaxAges, setTrainPaxErrors } from '../../redux/currentSearch/actions';
import { ErrorMessage } from './ErrorMessage';
import AgeField from '../FlightsManager/AgeField';

export const INFANT_MAX_AGE = 3;
const MOBILE_SEPARATOR_STYLE = { margin: '16px -24px 0 -24px' };

const TrainPaxSelectorContent = ({
    pax,
    setTrainPax,
    setTrainPaxAges,
    trackEvent,
    maxPax,
    paxAges,
    maxPaxAges,
    minPaxAges,
    paxErrors,
    setTrainPaxErrors,
    showErrors,
    hasSubmittedPax,
}) => {
    const { t } = useTranslation();
    const { seniors, adults, youth, children, infants = 0 } = pax;
    const { youthAges, childrenAges } = paxAges;
    const {
        maxPassengers,
        maxChildrenPerYouth,
        maxChildrenPerAdult,
        maxChildrenPerSenior,
        maxInfantsPerAdult = 1,
    } = maxPax;
    const {
        maxYouthAges,
        maxChildrenAges,
    } = maxPaxAges;
    const {
        minYouthAges,
        minChildrenAges,
    } = minPaxAges;
    const { isCssVariationMobile } = ABUtils.getABs();
    const showAgeSelector = isAgeSelectorEnabled();
    const displayErrors = showErrors || hasSubmittedPax;

    const getPaxRanges = ({ seniors, adults, youth, children, infants = 0 }) => {
        if (!showAgeSelector) {
            return {
                minAdults: 0,
                maxAdults: Math.max(0, maxPassengers - children - infants),
                maxChildren: Math.max(0, Math.min(
                    maxPassengers - adults - infants,
                    adults * maxChildrenPerAdult - infants
                )),
                maxInfantsPax: Math.max(0, Math.min(
                    maxPassengers - adults - children,
                    adults * maxChildrenPerAdult - children,
                    adults * maxInfantsPerAdult
                )),
            };
        }

        const maxChildrenByGroups = (maxChildrenPerYouth * youth) + (maxChildrenPerAdult * adults) + (maxChildrenPerSenior * seniors);
        const maxChildrenByCapacity = maxPassengers - seniors - adults - youth;

        return {
            maxSeniors: maxPassengers - adults - youth - children,
            maxAdults: maxPassengers - seniors - youth - children,
            minAdults: 0,
            maxYouth: maxPassengers - adults - seniors - children,
            maxChildren: Math.min(maxChildrenByGroups, maxChildrenByCapacity),
            maxInfants: Math.min(Math.floor(maxChildrenByGroups/2), maxChildrenByCapacity),
        };
    };

    const paxRange = getPaxRanges({ seniors, adults, youth, children, infants });

    const getChildrenValidation = useCallback(
        (ages) => {
            if (!ages || ages.length === 0) return null;

            const { maxChildren, maxInfants } = paxRange;

            const infantsCount = ages.filter((childrenAge) => childrenAge.age !== undefined && childrenAge.age <= INFANT_MAX_AGE).length;

            // Rule 1: Infant limit exceeded = ERROR
            if (infantsCount > maxInfants) {
                return { type: 'error', message: 'paxSelector.trains.infants.info' };
            }

            // Rule 2: Max children reached but infant limit respected = INFORMATIVE
            if (children === maxChildren && infantsCount <= maxInfants) {
                return { type: 'informative', message: 'paxSelector.trains.children.info' };
            }

            // Rule 3: Have infants but within limit = INFORMATIVE
            if (infantsCount > 0 && infantsCount <= maxInfants) {
                return { type: 'informative', message: 'paxSelector.trains.infants.info' };
            }

            return null;
        },
        [paxRange, children]
    );

    const shouldAgeFieldHaveError = (ageFieldIndex, ages) => {
        const { maxInfants } = paxRange;

        const currentAge = ages[ageFieldIndex]?.age;

        // Only check for error if current field has an infant age
        if (currentAge === undefined || currentAge > INFANT_MAX_AGE) {
            return false;
        }

        const infantsCount = ages.filter((childrenAge) => childrenAge.age !== undefined && childrenAge.age <= INFANT_MAX_AGE).length;
        // If we exceed the infant limit, mark this infant field as error
        if (infantsCount > maxInfants) {
            // Find the last infant that was added (highest index with infant age)
            const infantIndices = ages
                .map((age, idx) => ({ idx, age: age.age }))
                .filter((item) => item.age !== undefined && item.age <= INFANT_MAX_AGE)
                .map((item) => item.idx);

            const lastInfantIndex = Math.max(...infantIndices);
            return ageFieldIndex === lastInfantIndex;
        }

        return false;
    };
    const syncPaxToWindowMobile = useCallback((updatedPax) => {
        const shouldSync = isCssVariationMobile && window.paxState;
        if (!shouldSync) return;

        const { seniors = 0, adults = 1, youth = 0, children = 0 } = updatedPax || {};
        Object.assign(window.paxState, { seniors, adults, youth, children });
    }, [isCssVariationMobile]);

    const onPaxChange = (partial) => {
        const newPax = { ...pax, ...partial };

        if (!showAgeSelector) {
            const updatedPaxRange = getPaxRanges({ ...newPax });
            newPax.children = Math.min(newPax.children, updatedPaxRange.maxChildren);
            newPax.infants = Math.min(newPax.infants || 0, updatedPaxRange.maxInfantsPax);
            setTrainPax(newPax);
            return;
        }

        const newPaxAges = { ...paxAges };

        const updatedPaxRange = getPaxRanges({ ...newPax });
        const { minAdults, maxChildren } = updatedPaxRange;

        newPax.adults = Math.max(minAdults, newPax.adults);

        // Youth logic
        if (partial.youth !== undefined) {
            const newYouthAges = [...paxAges.youthAges];
            if (partial.youth > pax.youth) {
                newYouthAges.push({ id: uuidv4(), age: undefined });
            } else if (partial.youth < pax.youth) {
                newYouthAges.pop();
            }
            newPaxAges.youthAges = newYouthAges;
        }

        // Children logic
        if (partial.children !== undefined) {
            const newChildrenAges = [...paxAges.childrenAges];
            if (partial.children > pax.children) {
                newChildrenAges.push({ id: uuidv4(), age: undefined });
            } else if (partial.children < pax.children) {
                newChildrenAges.pop();
            }
            newPaxAges.childrenAges = newChildrenAges;
        } else {
            // If any other group changed, still need to slice childrenAges
            newPaxAges.childrenAges = paxAges.childrenAges.slice(0, maxChildren);
            newPax.children = Math.min(newPax.children, maxChildren);
        }

        setTrainPax(newPax);
        setTrainPaxAges(newPaxAges);
    };

    const onPaxAgesChange = (idx, age, ageGroup) => {
        const parsedAge = age === '' ? undefined : Number(age);
        const newPaxAges = { ...paxAges };
        switch (ageGroup) {
            case 'youth': {
                const newYouthAges = [...newPaxAges.youthAges];
                newYouthAges[idx].age = parsedAge;
                newPaxAges.youthAges = newYouthAges;
                break;
            }
            case 'children': {
                const newChildrenAges = [...newPaxAges.childrenAges];
                newChildrenAges[idx].age = parsedAge;
                newPaxAges.childrenAges = newChildrenAges;
                break;
            }
        }
        setTrainPaxAges(newPaxAges);
    };


    const getValidationObj = () => {
        const totalPax = seniors + adults + youth + children + infants;

        // 0. Zero passengers validation
        if (totalPax === 0) {
            return {
                validationMessage: 'paxSelector.trains.noPax.error',
                validationMessageType: 'odf-message-informative-scarcity'
            };
        }

        if (!showAgeSelector) {
            // Max passengers reached
            if (totalPax >= maxPassengers) {
                return {
                    validationMessage: 'paxSelector.info',
                    validationMessageType: 'odf-message-informative-highlight'
                };
            }
            return { validationMessage: '', validationMessageType: '' };
        }

        // 1. Children age validation errors
        const childrenValidation = getChildrenValidation(childrenAges);
        if (childrenValidation && childrenValidation.type === 'error') {
            return {
                validationMessage: childrenValidation.message,
                validationMessageType: 'odf-message-informative-scarcity'
            };
        }

        // 2. Capacity logic - Max passengers reached info
        if ((seniors + adults + youth + children) === maxPassengers) {
            return {
                validationMessage: 'paxSelector.trains.info',
                validationMessageType: 'odf-message-informative-highlight'
            };
        }

        // 3. Children age validation info
        if (childrenValidation && childrenValidation.type === 'informative') {
            return {
                validationMessage: childrenValidation.message,
                validationMessageType: 'odf-message-informative-highlight'
            };
        }

        return { validationMessage: '', validationMessageType: '' };
    };
    const { validationMessage, validationMessageType } = getValidationObj();


    if (validationMessage) {
        // TODO: (Rail-282) Revisit tracking logic to make it train specific.
        const label = TrackingUtils.LABEL.numberPaxInfo(validationMessage);
        trackEvent({ action: TrackingUtils.ACTION.FLIGHTS, label });
    }


    const errorState = useMemo(() => {
        const totalPax = seniors + adults + youth + children + infants;

        if (totalPax === 0) {
            return {
                paxSelectorLevelError: t('paxSelector.trains.noPax.error'),
                paxAgeFieldLevelError: undefined
            };
        }

        if (!showAgeSelector) {
            return {
                paxSelectorLevelError: undefined,
                paxAgeFieldLevelError: undefined
            };
        }

        const paxSelectorLevelValidation = getChildrenValidation(childrenAges);
        const paxAgeFieldLevelValidation =
            childrenAges.some((childrenAge) => childrenAge.age === undefined) ||
            youthAges.some((youthAge) => youthAge.age === undefined);

        return {
            paxSelectorLevelError: paxSelectorLevelValidation?.type === 'error' ? t(paxSelectorLevelValidation.message) : undefined,
            paxAgeFieldLevelError: paxAgeFieldLevelValidation ? t('paxSelector.trains.agefield.info') : undefined
        };
    }, [seniors, adults, youth, children, infants, childrenAges, youthAges, t, getChildrenValidation, showAgeSelector]);

    useEffect(() => {
        const isSelectorErrorChanged = paxErrors.paxSelectorLevelError !== errorState.paxSelectorLevelError;
        const isAgeErrorChanged = paxErrors.paxAgeFieldLevelError !== errorState.paxAgeFieldLevelError;

        if (isSelectorErrorChanged || isAgeErrorChanged) {
            setTrainPaxErrors({ ...paxErrors, ...errorState });
        }
    }, [errorState, paxErrors, setTrainPaxErrors]);

    // Sync modal pax to `window.paxState` real-time
    useEffect(() => {
        if (!pax) return;
        syncPaxToWindowMobile(pax);
    }, [pax, syncPaxToWindowMobile]);

    return (
        <>
            {showAgeSelector ? (
                <>
                    <PaxSelectorContentRow
                        paxKey="seniors"
                        cmsKey="seniors"
                        max={paxRange.maxSeniors}
                        value={pax.seniors}
                        onChange={onPaxChange}
                        isTrainsProduct
                    />
                    {isCssVariationMobile && (
                        <div className="odf-separator-dotted" style={MOBILE_SEPARATOR_STYLE} />
                    )}
                    <PaxSelectorContentRow
                        paxKey="adults"
                        cmsKey="adults"
                        min={paxRange.minAdults}
                        max={paxRange.maxAdults}
                        value={adults}
                        onChange={onPaxChange}
                        isTrainsProduct
                    />
                    {isCssVariationMobile && (
                        <div className="odf-separator-dotted" style={MOBILE_SEPARATOR_STYLE} />
                    )}
                    <PaxSelectorContentRow
                        paxKey="youth"
                        cmsKey="youth"
                        max={paxRange.maxYouth}
                        value={pax.youth}
                        onChange={onPaxChange}
                        isTrainsProduct
                    >
                        {youth > 0 &&
                         youthAges.map((youthAge, idx) => (
                             <AgeField
                                 key={youthAge.id}
                                 id={youthAge.id}
                                 index={idx}
                                 label={`${t('paxSelector.youth.singular')} ${idx + 1}`}
                                 value={youthAge.age !== undefined ? String(youthAge.age) : ''}
                                 min={minYouthAges}
                                 max={maxYouthAges}
                                 error={displayErrors && youthAge.age === undefined}
                                 onChange={(age) => onPaxAgesChange(idx, age, 'youth')}
                             />
                         ))
                        }
                    </PaxSelectorContentRow>
                    {isCssVariationMobile && (
                        <div className="odf-separator-dotted" style={MOBILE_SEPARATOR_STYLE} />
                    )}
                    <PaxSelectorContentRow
                        paxKey="children"
                        cmsKey="children"
                        max={paxRange.maxChildren}
                        value={pax.children}
                        onChange={onPaxChange}
                        isTrainsProduct
                    >
                        {children > 0 &&
                         childrenAges.map((childrenAge, idx) => (
                             <AgeField
                                 key={childrenAge.id}
                                 id={childrenAge.id}
                                 index={idx}
                                 label={`${t('paxSelector.children.singular')} ${idx + 1}`}
                                 value={childrenAge.age !== undefined ? String(childrenAge.age) : ''}
                                 min={minChildrenAges}
                                 max={maxChildrenAges}
                                 error={displayErrors && (childrenAge.age === undefined || shouldAgeFieldHaveError(idx, childrenAges))}
                                 onChange={(age) => onPaxAgesChange(idx, age, 'children')}
                             />
                         ))
                        }
                    </PaxSelectorContentRow>
                </>
            ) : (
                <>
                    <PaxSelectorContentRow
                        paxKey="adults"
                        cmsKey="adults"
                        min={paxRange.minAdults}
                        max={paxRange.maxAdults}
                        value={adults}
                        onChange={onPaxChange}
                    />
                    {isCssVariationMobile && (
                        <div className="odf-separator-dotted" />
                    )}
                    <PaxSelectorContentRow
                        paxKey="children"
                        cmsKey="children"
                        min={0}
                        max={paxRange.maxChildren}
                        value={children}
                        onChange={onPaxChange}
                    />
                    {isCssVariationMobile && (
                        <div className="odf-separator-dotted" />
                    )}
                    <PaxSelectorContentRow
                        paxKey="infants"
                        cmsKey="infants"
                        min={0}
                        max={paxRange.maxInfantsPax}
                        value={infants}
                        onChange={onPaxChange}
                    />
                </>
            )}
            {validationMessage && <ErrorMessage message={validationMessage} type={validationMessageType} />}
            {displayErrors && paxErrors.paxAgeFieldLevelError && (
                <div className="odf-space-outer-top-m">
                    <ErrorMessage
                        message="paxSelector.trains.agefield.info"
                        type="odf-message-informative-scarcity"
                    />
                </div>
            )}
        </>
    );
};

TrainPaxSelectorContent.propTypes = {
    pax: TrainPaxProps.isRequired,
    paxAges: TrainPaxAgesProps.isRequired,
    maxPax: TrainMaxPaxProps,
    maxPaxAges: TrainMaxPaxAgesProps,
    minPaxAges: TrainMinPaxAgesProps,
    paxErrors: TrainPaxErrorsProps,
    setTrainPax: PropTypes.func.isRequired,
    setTrainPaxAges: PropTypes.func.isRequired,
    setTrainPaxErrors: PropTypes.func.isRequired,
    trackEvent: PropTypes.func.isRequired,
    showErrors: PropTypes.bool,
    hasSubmittedPax: PropTypes.bool,
};

TrainPaxSelectorContent.defaultProps = {
    maxPax: TrainMaxPaxPropsDefaults,
    maxPaxAges: TrainMaxPaxAgesPropsDefaults,
    minPaxAges: TrainMinPaxAgesPropsDefaults,
    paxErrors: TrainPaxErrorsPropsDefaults,
    showErrors: false,
    hasSubmittedPax: false,
};

const withContext = withPageContext(TrainPaxSelectorContent);
export { withContext as TrainPaxSelectorContent };

const mapStateToProps = createStructuredSelector({
    pax: ({ currentSearch }) => currentSearch.trainPax,
    paxAges: ({ currentSearch }) => currentSearch.trainPaxAges,
    paxErrors: ({ currentSearch }) => currentSearch.trainPaxErrors,
    showErrors: ({ currentSearch }) => currentSearch.segments?.some((segment) => segment.showErrors) || false,
});

export const TrainConnectedPaxSelectorContent = connect(mapStateToProps, {
    setTrainPax,
    setTrainPaxAges,
    setTrainPaxErrors,
})(withContext);
